import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { commandCenterApi, type AlertDto, type AlertSeverity } from '../api';
import { useCommandCenter } from '../store';

const SEVERITIES: AlertSeverity[] = ['CRITICAL', 'WARNING', 'INFO'];

export function AlertFeed() {
  const alerts = useCommandCenter((s) => s.alerts);
  const upsertAlert = useCommandCenter((s) => s.upsertAlert);
  const select = useCommandCenter((s) => s.select);
  const qc = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState<Set<AlertSeverity>>(new Set());
  const [hideResolved, setHideResolved] = useState(true);

  const ack = useMutation({
    mutationFn: (alertId: string) => commandCenterApi.acknowledgeAlert(alertId),
    onSuccess: (a) => {
      upsertAlert(a);
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const resolve = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      commandCenterApi.resolveAlert(id, note),
    onSuccess: (a) => {
      upsertAlert(a);
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const filtered = alerts.filter((a) => {
    if (hideResolved && a.status === 'RESOLVED') return false;
    if (severityFilter.size && !severityFilter.has(a.severity)) return false;
    return true;
  });

  return (
    <div className="flex h-full flex-col border-l border-cc-border bg-cc-surface text-cc-fg">
      <div className="border-b border-cc-border px-3 py-2">
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-[10px] font-semibold uppercase tracking-wider text-cc-muted">
            Alerts · {filtered.length}
          </h2>
          <label className="flex items-center gap-1 font-mono text-[10px] text-cc-muted">
            <input
              type="checkbox"
              checked={hideResolved}
              onChange={(e) => setHideResolved(e.target.checked)}
              className="h-3 w-3 accent-cc-accent"
            />
            hide resolved
          </label>
        </div>
        <div className="mt-2 flex gap-1">
          {SEVERITIES.map((sev) => {
            const active = severityFilter.has(sev);
            return (
              <button
                key={sev}
                onClick={() => {
                  const next = new Set(severityFilter);
                  if (next.has(sev)) next.delete(sev);
                  else next.add(sev);
                  setSeverityFilter(next);
                }}
                className={`rounded-none border px-1.5 py-0.5 font-mono text-[10px] uppercase ${
                  active
                    ? sev === 'CRITICAL'
                      ? 'border-cc-crit bg-cc-crit/20 text-cc-crit'
                      : sev === 'WARNING'
                        ? 'border-cc-warn bg-cc-warn/20 text-cc-warn'
                        : 'border-cc-accent bg-cc-accent/20 text-cc-accent'
                    : 'border-cc-border bg-cc-bg text-cc-muted hover:text-cc-fg'
                }`}
              >
                {sev}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {filtered.length === 0 ? (
          <p className="p-4 font-mono text-xs text-cc-muted">[ NO ACTIVE ALERTS ]</p>
        ) : (
          filtered.map((alert) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              onAck={() => ack.mutate(alert.id)}
              onResolve={(note) => resolve.mutate({ id: alert.id, note })}
              onJump={() => alert.asset_id && select(alert.asset_id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AlertRow({
  alert,
  onAck,
  onResolve,
  onJump,
}: {
  alert: AlertDto;
  onAck: () => void;
  onResolve: (note?: string) => void;
  onJump: () => void;
}) {
  const tone =
    alert.severity === 'CRITICAL'
      ? 'border-l-cc-crit'
      : alert.severity === 'WARNING'
        ? 'border-l-cc-warn'
        : 'border-l-cc-accent';
  const resolved = alert.status === 'RESOLVED';
  return (
    <div
      className={`border-b border-l-2 border-cc-border ${tone} p-3 font-mono text-xs ${
        resolved ? 'opacity-50' : ''
      }`}
    >
      <button onClick={onJump} className="block w-full text-left hover:text-cc-accent">
        <div className="flex items-center justify-between">
          <span className="font-semibold uppercase">{alert.alert_type}</span>
          <span className="text-cc-muted">{new Date(alert.raised_at).toLocaleTimeString()}</span>
        </div>
        <p className="mt-1 text-cc-fg">{alert.title}</p>
        <p className="mt-0.5 text-cc-muted">{alert.message}</p>
      </button>
      {!resolved && (
        <div className="mt-2 flex gap-2">
          {alert.status === 'OPEN' && (
            <button
              onClick={onAck}
              className="border border-cc-border px-2 py-0.5 text-[10px] uppercase text-cc-muted hover:border-cc-accent hover:text-cc-accent"
            >
              Acknowledge
            </button>
          )}
          <button
            onClick={() => onResolve()}
            className="border border-cc-border px-2 py-0.5 text-[10px] uppercase text-cc-muted hover:border-cc-ok hover:text-cc-ok"
          >
            Resolve
          </button>
        </div>
      )}
      {resolved && alert.resolution_note && (
        <p className="mt-1 text-[10px] italic text-cc-muted">“{alert.resolution_note}”</p>
      )}
    </div>
  );
}
